<script setup lang="ts">
import type { CmsElementBioCircle } from "~/composables/useCustom";
import SharedHeadline from "~/components/shared/SharedHeadline.vue";
import SharedTopline from "~/components/shared/SharedTopline.vue";
import { Carousel, Slide, Navigation } from "vue3-carousel";
import { getCategoryRoute } from "@shopware/helpers";

const props = defineProps<{
  content: CmsElementBioCircle;
}>();

const settings = {
  itemsToShow: 5,
  itemsToScroll: 5,
  wrapAround: false,
  snapAlign: "center",
  autoplay: 0,
  transition: 600,
  pauseAutoplayOnHover: true,
  breakpoints: {
    // 200px and up
    200: {
      itemsToShow: 3,
      itemsToScroll: 3,
      snapAlign: "center",
    },
    600: {
      itemsToShow: 5,
      itemsToScroll: 5,
      snapAlign: "center",
    },
  },
};

const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

const productSlider = ref(null);
const currentSlide = ref(0);

const { headlineText, toplineText, toplineType, headlineType, Slides } =
  props.content?.data;
</script>
<template>
  <div class="logo-slider-wrapper">
    <div class="container px-0 xl-px-5">
      <div class="mb-10 lg:mb-[3.75rem]">
        <SharedTopline
          :headline="toplineText"
          :headlineType="toplineType"
        ></SharedTopline>
        <SharedHeadline
          :headline="headlineText"
          :headlineType="headlineType"
        ></SharedHeadline>
      </div>
      <div
        id="ClientsCarousel"
        class="md:-mx-[2.5rem] [&>.slick-list>.slick-track]:flex [&>.slick-list>.slick-track]:items-center [&>.slick-list]:overflow-visible md:[&>.slick-list>.slick-track>.slick-slide.prev]:opacity-0 md:[&>.slick-list>.slick-track>.slick-slide]:transition-all md:[&>.slick-list>.slick-track>.slick-slide]:duration-75"
      >
        <Carousel v-bind="settings" ref="productSlider" v-model="currentSlide">
          <Slide v-for="slide of Slides" :key="slide.id">
            <div class="px-[2.2rem]">
              <NuxtLink
                v-if="slide.slideLink && slide.slideLink.type === 'internal'"
                :to="formatLink(getCategoryRoute(slide.slideLink.link))"
              >
                <NuxtImg
                  format="webp"
                  loading="lazy"
                  :src="slide.slideImage.media.url"
                  :alt="slide.slideImage.media.metaData.alt"
                  :height="slide.slideImage.media.metaData.height"
                  :width="slide.slideImage.media.metaData.width"
                  class="min-w-[100px]"
                />
              </NuxtLink>
              <NuxtLink
                v-if="slide.slideLink && slide.slideLink.type === 'external'"
                :to="slide.slideLink.url"
                target="_blank"
              >
                <NuxtImg
                  format="webp"
                  loading="lazy"
                  :src="slide.slideImage.media.url"
                  :alt="slide.slideImage.media.metaData.alt"
                  :height="slide.slideImage.media.metaData.height"
                  :width="slide.slideImage.media.metaData.width"
                  class="min-w-[100px]"
                />
              </NuxtLink>
              <NuxtImg
                v-if="!slide.slideLink"
                format="webp"
                loading="lazy"
                :src="slide.slideImage.media.url"
                :alt="slide.slideImage.media.metaData.alt"
                :height="slide.slideImage.media.metaData.height"
                :width="slide.slideImage.media.metaData.width"
                class="min-w-[100px]"
              />
            </div>
          </Slide>
          <template #addons>
            <div
              class="carousel__navigation-button pointer-events-none z-0 absolute bottom-0 md:bottom-auto top-1/4 md:top-2/4 max-w-[90rem] flex justify-between desktop:-left-[60px] desktop:-right-[60px] -left-5 -right-5 mx-auto px-1 md:px-3 laptop:px-10 [&>button]:static"
            >
              <Navigation>
                <template #next>
                  <img
                    src="assets/icons/slide-right.svg"
                    alt="Next"
                    class="w-6 h-6"
                  />
                </template>
                <template #prev>
                  <img
                    src="assets/icons/slide-left.svg"
                    alt="Previous"
                    class="w-6 h-6"
                  />
                </template>
              </Navigation>
            </div>
          </template>
        </Carousel>
      </div>
    </div>
  </div>
</template>

<style scoped>
.carousel__navigation-button :deep(.carousel__prev),
.carousel__navigation-button :deep(.carousel__next) {
  @apply bg-skin-icon text-skin-white w-12 h-12 rounded-full flex items-center justify-center;
  position: relative;
  pointer-events: all;
}
</style>
